import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography
} from '@mui/material';

const AddPayloadDialog = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    type: '',
    customType: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    nickname: '',
    weight: '',
    status: 'active',
    purchaseDate: '',
    lastCalibrationDate: '',
    compatibleDroneModels: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const typeOptions = [
    { value: 'camera', label: 'Camera' },
    { value: 'lidar', label: 'LiDAR' },
    { value: 'multispectral', label: 'Multispectral' },
    { value: 'thermal', label: 'Thermal' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'sprayer', label: 'Sprayer' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' },
    { value: 'damaged', label: 'Damaged' }
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.type?.trim()) {
      newErrors.type = 'Type is required';
    }
    if (formData.type === 'other' && !formData.customType?.trim()) {
      newErrors.customType = 'Custom type is required when Other is selected';
    }
    if (!formData.manufacturer?.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    if (!formData.model?.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.serialNumber?.trim()) {
      newErrors.serialNumber = 'Serial number is required';
    }
    if (!formData.weight || formData.weight <= 0) {
      newErrors.weight = 'Valid weight is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const dataToSubmit = {
      ...formData,
      type: formData.type === 'other' ? formData.customType.trim() : formData.type,
      weight: parseFloat(formData.weight),
      nickname: formData.nickname?.trim() || null,
      purchaseDate: formData.purchaseDate || null,
      lastCalibrationDate: formData.lastCalibrationDate || null,
      compatibleDroneModels: formData.compatibleDroneModels?.trim() || null,
      notes: formData.notes?.trim() || null
    };

    // Remove customType from submission as it's merged into type
    delete dataToSubmit.customType;

    onAdd(dataToSubmit);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Payload / Sensor</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              select
              label="Payload Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              error={!!errors.type}
              helperText={errors.type}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {formData.type === 'other' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Custom Payload Type"
                value={formData.customType}
                onChange={(e) => handleChange('customType', e.target.value)}
                error={!!errors.customType}
                helperText={errors.customType || 'Enter custom payload type'}
                placeholder="e.g., Radar, Magnetometer"
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => handleChange('manufacturer', e.target.value)}
              error={!!errors.manufacturer}
              helperText={errors.manufacturer}
              placeholder="e.g., DJI, Zenmuse, MicaSense"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Model"
              value={formData.model}
              onChange={(e) => handleChange('model', e.target.value)}
              error={!!errors.model}
              helperText={errors.model}
              placeholder="e.g., Zenmuse H20T, RedEdge-MX"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Serial Number"
              value={formData.serialNumber}
              onChange={(e) => handleChange('serialNumber', e.target.value)}
              error={!!errors.serialNumber}
              helperText={errors.serialNumber}
              placeholder="Unique serial number"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nickname (Optional)"
              value={formData.nickname}
              onChange={(e) => handleChange('nickname', e.target.value)}
              placeholder="e.g., Thermal Camera 1, Survey Camera"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Weight (grams)"
              value={formData.weight}
              onChange={(e) => handleChange('weight', e.target.value)}
              error={!!errors.weight}
              helperText={errors.weight}
              placeholder="e.g., 500"
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>

          {/* Status and Dates */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status and Dates
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Purchase Date"
              value={formData.purchaseDate}
              onChange={(e) => handleChange('purchaseDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Last Calibration Date"
              value={formData.lastCalibrationDate}
              onChange={(e) => handleChange('lastCalibrationDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Compatibility & Notes
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Compatible Drone Models"
              value={formData.compatibleDroneModels}
              onChange={(e) => handleChange('compatibleDroneModels', e.target.value)}
              placeholder="e.g., DJI Mavic 3 Enterprise, DJI Matrice 300"
              helperText="Comma-separated list of compatible drone models"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes about this payload/sensor..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Add Payload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPayloadDialog;
